import Checkbox from '@creditflowresearch/uilib/Checkbox';

import CheckboxField from '@/creditflowresearch/ui/common/components/organisms/Filters/Checkboxer/CheckboxFieldContainer';
import FilterColumns from '@/creditflowresearch/ui/common/components/organisms/Filters/components/FilterColumns';
import FilterItem from '@/creditflowresearch/ui/common/components/organisms/Filters/components/FilterItem';
import FilterSection from '@/creditflowresearch/ui/common/components/organisms/Filters/components/FilterSection';

import style from '@/creditflowresearch/ui/common/components/organisms/Filters/filters.module.scss';
import withTooltipOnInputLabel from '@framework/ui/Molecules/Form/withTooltipOnInputLabel';

const CheckboxWithTooltipOnLabel = withTooltipOnInputLabel(Checkbox);

interface IProps {
  columnsCount?: number,
  labelWithAbbreviation?: boolean,
  isAbbreviationInLabel?: boolean,
  itemKeyPrefix?: string,
  items: any[],
  sectionId?: string,
  absoluteFilterName?: string,
  absoluteFilter?: boolean,
  absoluteFilterLabel?: string,
  absoluteFilterTooltipContent?: string,
  sectionTitle?: string,
  isVisible?: boolean,
  onSectionToggle?: () => void,
  filterName?: string,
  isFilterChecked?: (filterName: string, id: string | number) => boolean,
  handleFilterUpdate?: (filter: { [key in string]: string }) => void,
  handleParentFilterChange?: (checkbox: any, name: string, parentIds: number[]) => void,
  handleFilterByChange?: (filterName: string, ids: string[] | number[]) => void,
  CheckboxComponent?: React.ElementType,
}

function getLabel(item, {
  isAbbreviationInLabel,
  labelWithAbbreviation,
  itemKeyPrefix,
}) {
  if (isAbbreviationInLabel) {
    return item[`${ itemKeyPrefix }Abbreviation`];
  }
  if (labelWithAbbreviation) {
    return `${ item[`${ itemKeyPrefix }Name`] } (${ item[`${ itemKeyPrefix }Abbreviation`] })`;
  }

  return item[`${ itemKeyPrefix }Name`];
}

/**
 * @param {Object} props
 * @return {ReactElement}
 */
function OneLevelList(props: IProps) {
  const {
    sectionId,
    sectionTitle,
    isVisible,
    filterName,
    columnsCount = 2,
    labelWithAbbreviation = false,
    isAbbreviationInLabel = false,
    itemKeyPrefix,
    absoluteFilterName,
    absoluteFilter,
    absoluteFilterLabel,
    absoluteFilterTooltipContent,
    CheckboxComponent = Checkbox,
  } = props;

  const children = props.items.map((item) => ({
    id: String(item[`${ itemKeyPrefix }Id`] ?? item?.value),
    label: getLabel(item, {
      isAbbreviationInLabel,
      labelWithAbbreviation,
      itemKeyPrefix,
    }) || item?.name,
    disabled: !item.canChangeResponse,
  }));
  const activeChildren = children.filter((child) => !child.disabled);

  return (
    <FilterSection
      sectionId={ sectionId }
      headerComponent={ (
        <>
          <CheckboxField
            name={ sectionId }
            dataTest={ sectionId }
            component={ CheckboxComponent }
            label={ sectionTitle }
            supportIndeterminate
            disabled={ activeChildren.length === 0 }
            onChange={ (checkbox) => props.handleParentFilterChange(checkbox, filterName, []) }
          />
          { absoluteFilterName && (
            <CheckboxField
              name={ absoluteFilterName }
              dataTest={ absoluteFilterName }
              component={ CheckboxWithTooltipOnLabel }
              label={ absoluteFilterLabel }
              // @ts-ignore
              labelTooltipContent={ absoluteFilterTooltipContent }
              defaultChecked={ absoluteFilter }
              className={ style.absoluteFilter }
              labelClassName={ style.absoluteFilterLabel }
              onChange={ (checkbox) => props.handleFilterUpdate({
                [absoluteFilterName]: checkbox.checked,
              }) }
            />
          ) }
        </>
      ) }
      isVisible={ isVisible }
      onSectionToggle={ props.onSectionToggle }
    >
      <FilterColumns columnsCount={ columnsCount }>
        { children.map(({
          id,
          label,
          disabled,
        }) => (
          <FilterItem
            key={ id }
            isExpandable={ false }
          >
            <CheckboxField
              parent={ sectionId }
              name={ id }
              dataTest={ id }
              component={ CheckboxComponent }
              label={ label }
              disabled={ disabled }
              defaultChecked={ props.isFilterChecked(filterName, id) }
              onChange={ () => props.handleFilterByChange(filterName, [id]) }
            />
          </FilterItem>
        )) }
      </FilterColumns>
    </FilterSection>
  );
}

export default OneLevelList;
